/**
 * Selection state for the AI suggestion diff drawer (Phase F-finale-4 Slice 5).
 *
 * Flat per-node selection over the nodes produced by {@link collectNodes}.
 * Invalid nodes (failed validation / disabled module) can never be selected —
 * `toggle` and `selectAll` both refuse them, so the `acceptedNodeIds` handed
 * to the apply route only ever contains nodes the BE will accept.
 *
 * Initial state pre-selects every valid node (the diff drawer opens with all
 * acceptable nodes checked, matching the F4 UX contract).
 */

import { useCallback, useMemo, useState } from "react";

import type { CapabilitySuggestion } from "@eduviaos/shared";

import { collectNodes, type SelectableNode } from "../lib/collectNodes.js";

export interface SuggestionSelection {
  /** All nodes in render order (valid and invalid). */
  nodes: SelectableNode[];
  /** Node metadata by `node_id`, for composite renderers. */
  nodeById: Map<string, SelectableNode>;
  /** Count of selectable (valid) nodes. */
  selectableCount: number;
  isSelected(nodeId: string): boolean;
  /** Toggle a node; no-op for invalid nodes. */
  toggle(nodeId: string): void;
  /** Select every valid node. */
  selectAll(): void;
  /** Clear the selection. */
  deselectAll(): void;
  /** The node ids to send to the apply route (selected ∩ valid). */
  acceptedNodeIds: string[];
}

function validIds(nodes: SelectableNode[]): string[] {
  return nodes.filter((n) => n.isValid).map((n) => n.node_id);
}

export function useSuggestionSelection(suggestion: CapabilitySuggestion): SuggestionSelection {
  const nodes = useMemo(() => collectNodes(suggestion), [suggestion]);
  const validById = useMemo(() => new Map(nodes.map((n) => [n.node_id, n.isValid])), [nodes]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set(validIds(nodes)));

  const isSelected = useCallback((nodeId: string) => selected.has(nodeId), [selected]);

  const toggle = useCallback(
    (nodeId: string) => {
      if (!validById.get(nodeId)) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      });
    },
    [validById],
  );

  const selectAll = useCallback(() => setSelected(new Set(validIds(nodes))), [nodes]);
  const deselectAll = useCallback(() => setSelected(new Set()), []);

  const acceptedNodeIds = useMemo(
    () => nodes.filter((n) => n.isValid && selected.has(n.node_id)).map((n) => n.node_id),
    [nodes, selected],
  );

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.node_id, n])), [nodes]);

  return {
    nodes,
    nodeById,
    selectableCount: validById.size === 0 ? 0 : validIds(nodes).length,
    isSelected,
    toggle,
    selectAll,
    deselectAll,
    acceptedNodeIds,
  };
}
